// ====================================================
// src/components/block/liveStreamManagement/LevelSlider.tsx
//
// Potentiomètre linéaire générique (libellé | piste avec graduations | valeur, sur UNE
// ligne) — même piste/poignée/graduations dessinées à la main que le fader audio
// (ChannelStrip/FaderBar dans SourceConfigModal), même gris, mêmes hauteurs. Utilisé
// pour les réglages vidéo (luminosité/contraste/balance des blancs) ET pour les
// paramètres des effets audio (fréquence du lowcut, mix de la reverb...) — un seul
// composant, pas de dérive visuelle entre les deux panneaux.
// ====================================================

import React, { useRef } from 'react';

const LEVEL_BAR_H    =  8;
const LEVEL_SLIDER_H = 16;
const LEVEL_HIT_H    = 24;
const LEVEL_HIT_PAD  =  8;
const LEVEL_DEFAULT  = 50;
const LEVEL_LABEL_W  = 132;  // colonne du libellé — aligne plusieurs pistes entre elles
const LEVEL_VALUE_W  = 52;   // colonne de la valeur — assez large pour "8000K", pas juste "50"
const LEVEL_TICKS    = [0, 25, 50, 75, 100];

export const LevelSlider: React.FC<{
  label: string; value: number; onChange: (v: number) => void;
  /** Par défaut 0-100/50 — la balance des blancs par exemple redéfinit tout ça en
   * kelvin (3000-8000, défaut 5500) sans toucher au tracé ni à l'interaction. */
  min?: number; max?: number; defaultValue?: number;
  /** Arrondi de la valeur déposée pendant le drag — évite des valeurs du genre « 5511 »
   * qui ne veulent rien dire à l'œil sur une plage large. */
  roundTo?: number;
  /** Formatage de la valeur affichée à droite (ex: `${v} K`, `${v} Hz`) — brut par défaut. */
  format?: (v: number) => string;
}> = ({ label, value, onChange, min = 0, max = 100, defaultValue = LEVEL_DEFAULT, roundTo = 1, format }) => {
  const trackRef    = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const span  = max - min;
  const step  = span / 100; // même pas relatif que l'ancienne échelle 0-100 (1 par flèche)
  const pct   = ((value - min) / span) * 100;

  const posFromClientX = (clientX: number): number => {
    const el = trackRef.current;
    if (!el) return value;
    const r = el.getBoundingClientRect();
    const p = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    const raw = min + p * span;
    return Math.round(raw / roundTo) * roundTo;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const delta =
      e.key === 'ArrowRight' || e.key === 'ArrowUp'   ?  step :
      e.key === 'ArrowLeft'  || e.key === 'ArrowDown' ? -step :
      e.key === 'Home' ? -span : e.key === 'End' ? span : 0;
    if (!delta) return;
    e.preventDefault();
    onChange(Math.min(max, Math.max(min, value + delta)));
  };

  const displayValue = format ? format(value) : String(value);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: `${LEVEL_LABEL_W}px 1fr ${LEVEL_VALUE_W}px`, alignItems: 'center', columnGap: 16 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#cbd5e1' }}>{label}</span>
      <div
        role           = "slider"
        tabIndex       = {0}
        aria-label     = {label}
        aria-valuemin  = {min}
        aria-valuemax  = {max}
        aria-valuenow  = {value}
        title          = {`${label} : ${displayValue} — double-clic pour revenir à ${format ? format(defaultValue) : defaultValue}`}
        onPointerDown  = {e => { e.preventDefault(); e.currentTarget.focus(); draggingRef.current = true; e.currentTarget.setPointerCapture(e.pointerId); onChange(posFromClientX(e.clientX)); }}
        onPointerMove  = {e => { if (draggingRef.current) onChange(posFromClientX(e.clientX)); }}
        onPointerUp    = {e => { draggingRef.current = false; try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {} }}
        onPointerCancel= {() => { draggingRef.current = false; }}
        onDoubleClick  = {() => onChange(defaultValue)}
        onKeyDown      = {handleKeyDown}
        onDragStart    = {e => e.preventDefault()}
        draggable      = {false}
        style          = {{
          position        : 'relative',
          height          : LEVEL_HIT_H,
          marginLeft      : -LEVEL_HIT_PAD,
          marginRight     : -LEVEL_HIT_PAD,
          cursor          : 'pointer',
          touchAction     : 'none',
          outline         : 'none',
          userSelect      : 'none',
          WebkitUserSelect: 'none',
        }}
      >
        <div ref={trackRef} style={{ position: 'absolute', left: LEVEL_HIT_PAD, right: LEVEL_HIT_PAD, top: 0, bottom: 0 }}>
          {/* piste — même gris, mêmes graduations que le fader audio (DB_MARKS/dbToBarPct) */}
          <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: LEVEL_BAR_H, background: '#000', borderRadius: LEVEL_BAR_H / 2, overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, width: `${pct}%`, background: 'rgba(148,163,184,0.35)' }} />
            {LEVEL_TICKS.map(t => (
              <div key={t} style={{ position: 'absolute', left: `${t}%`, top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,0.25)', pointerEvents: 'none' }} />
            ))}
          </div>
          {/* poignée */}
          <div style={{ position: 'absolute', left: `${pct}%`, top: '50%', transform: 'translate(-50%, -50%)', width: 10, height: LEVEL_SLIDER_H, borderRadius: 3, background: '#e2e8f0', boxShadow: '0 0 3px rgba(0,0,0,0.6)', pointerEvents: 'none' }} />
        </div>
      </div>
      <span title="Valeur actuelle" style={{ fontSize: 10, fontFamily: 'monospace', color: 'rgba(255,255,255,0.5)', textAlign: 'right' }}>{displayValue}</span>
    </div>
  );
};
